/**
 * Impact punch (pure): turns one landed hit into how hard it should feel.
 *
 *   strength = feel weight × damage-size factor, + crit and killing-blow bonuses, + a little per
 *              extra target for AoE that hits many.
 *
 * From strength: hit-stop (ms), camera trauma, a directional camera kick, an FOV punch, the
 * enemy's flinch / flash, the grade "impact frame" and the sound gain. Only the local player's
 * own hits move the camera; healers and casters (`ranged`) get a softer camera. Everything is
 * multiplied by the effects-intensity setting by the caller's shake / hit-stop objects.
 */

export interface PunchInput {
  /** Ability feel weight 0..1 (feel/table.ts). */
  weight: number;
  crit: boolean;
  /** The hit killed its target. */
  kill: boolean;
  /** Targets this cast hit so far (AoE), ≥ 1. */
  targets: number;
  /** Damage relative to this source's recent average (1 = typical). */
  rel: number;
  /** The local player dealt it. */
  mine: boolean;
  /** No local player (spectating): party hits get a share of the camera. */
  spectating: boolean;
  /** Caster / healer ability (softer camera). */
  ranged: boolean;
  finisher: boolean;
}

export interface Punch {
  /** 0..1.5 overall strength. */
  strength: number;
  /** Hit-stop (ms; 0 = none). */
  stopMs: number;
  /** Camera trauma to add (0 = none). */
  trauma: number;
  /** Directional camera kick (yards along the hit direction, screen space). */
  kick: number;
  /** FOV punch (degrees, positive = zoom in). */
  fov: number;
  /** Enemy flinch amplitude 0..1.5 (knock / squash / stagger). */
  flinch: number;
  /** Mesh flash 0..1. */
  flash: number;
  /** Grade "impact frame" (contrast punch) 0..1. */
  frame: number;
  /** Impact sound gain multiplier. */
  gain: number;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Damage-size factor: a hit twice the source's average lands harder, a weak tick softer. */
export function sizeFactor(rel: number): number {
  if (!Number.isFinite(rel) || rel <= 0) return 1;
  return clamp(0.72 + 0.28 * Math.sqrt(rel), 0.75, 1.4);
}

export function punchStrength(i: PunchInput): number {
  let s = i.weight * sizeFactor(i.rel);
  if (i.crit) s = s * 1.3 + 0.08;
  if (i.kill) s += i.finisher ? 0.3 : 0.15;
  if (i.targets > 1) s += Math.min(0.2, 0.04 * (i.targets - 1));
  return clamp(s, 0, 1.5);
}

/** Fill `out` for one landed hit. `out` is reused (no allocation). */
export function computePunch(i: PunchInput, out: Punch): Punch {
  const s = punchStrength(i);
  out.strength = s;
  const cam = i.mine ? 1 : i.spectating ? 0.45 : 0;
  const soft = i.ranged ? 0.5 : 1;
  // Hit-stop only for the player's own meaningful hits: tiny freezes on every auto feel like lag.
  let stop = 0;
  if (i.mine && s >= 0.38) {
    stop = 22 + 70 * Math.min(1, (s - 0.38) / 0.7);
    if (i.crit) stop += 12;
    if (i.kill && i.finisher) stop += 25;
    stop *= i.ranged ? 0.55 : 1;
  } else if (i.spectating && s >= 0.7) stop = 30;
  out.stopMs = Math.round(Math.min(130, stop));
  out.trauma = s < 0.25 ? 0 : cam * soft * (0.04 + 0.26 * s * s);
  out.kick = cam * soft * (s < 0.3 ? 0 : 0.05 + 0.12 * Math.min(1.2, s));
  out.fov = i.mine && !i.ranged && s >= 0.55 ? 0.5 + 1.1 * Math.min(1, (s - 0.55) / 0.6) : 0;
  out.flinch = clamp(0.22 + 0.78 * s * (i.mine || i.spectating ? 1 : 0.75), 0.2, 1.5);
  out.flash = clamp(0.5 + 0.5 * s + (i.crit ? 0.2 : 0), 0, 1);
  out.frame = i.mine && (i.crit || (i.kill && i.finisher)) && s >= 0.5 ? clamp(0.5 + 0.5 * (s - 0.5), 0, 1) : 0;
  out.gain = clamp(0.7 + 0.35 * s, 0.6, 1.25);
  return out;
}

export function emptyPunch(): Punch {
  return { strength: 0, stopMs: 0, trauma: 0, kick: 0, fov: 0, flinch: 0, flash: 0, frame: 0, gain: 1 };
}

/**
 * Running average hit size per source (non-tick damage), so "a big hit" adapts to gear and key
 * level without knowing max HP. Fixed capacity, no allocation after warm-up (entries are reused).
 */
export class DamageScale {
  private readonly avg = new Map<string, number>();

  constructor(
    private readonly alpha = 0.15,
    private readonly cap = 64,
  ) {}

  /** Size of `amount` relative to the source's average (then folds it into the average). */
  observe(sourceId: string, amount: number): number {
    if (!(amount > 0)) return 1;
    const a = this.avg.get(sourceId);
    if (a === undefined) {
      if (this.avg.size >= this.cap) this.avg.clear();
      this.avg.set(sourceId, amount);
      return 1;
    }
    const rel = amount / Math.max(1e-6, a);
    this.avg.set(sourceId, a + (amount - a) * this.alpha);
    return rel;
  }

  clear(): void {
    this.avg.clear();
  }
}
